import React, { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import {
  ShieldAlert,
  PanelLeftClose,
  PanelLeftOpen,
  ChevronRight,
} from "lucide-react";
import { MENU_GROUPS } from "@/config/menuConfig";

const STORAGE_KEY = "leftnav.openGroups.v2";

export const LeftNav = ({ expanded = true, onToggle }) => {
  const ToggleIcon = expanded ? PanelLeftClose : PanelLeftOpen;

  // Multi-open accordion. Default: ONLY the Overview group is open.
  // User toggles are persisted so reloads remember their preference.
  const [openGroups, setOpenGroups] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const arr = JSON.parse(saved);
        if (Array.isArray(arr)) return new Set(arr);
      }
    } catch (_e) { /* ignore */ }
    return new Set(["overview"]);
  });

  // Persist openGroups
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(openGroups)));
    } catch (_e) { /* ignore */ }
  }, [openGroups]);

  const handleGroupClick = (key) => {
    setOpenGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  return (
    <aside
      className={[
        "hidden md:flex flex-col border-r border-border bg-[hsl(var(--surface))] shrink-0 transition-[width] duration-200",
        expanded ? "w-56" : "w-14",
      ].join(" ")}
      data-testid="left-nav"
      data-expanded={expanded}
    >
      {/* Brand + toggle */}
      <div
        className={[
          "h-14 border-b border-border flex items-center shrink-0",
          expanded ? "px-3 gap-2 justify-between" : "px-2 justify-center",
        ].join(" ")}
      >
        {expanded && (
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <div className="w-7 h-7 bg-[hsl(var(--primary))] flex items-center justify-center shrink-0">
              <ShieldAlert className="w-4 h-4 text-[hsl(var(--primary-foreground))]" />
            </div>
            <span
              className="font-display font-black text-[12px] leading-tight break-words"
              data-testid="nav-brand"
            >
              AI Stadium Surveillance
            </span>
          </div>
        )}
        <button
          onClick={onToggle}
          aria-label={expanded ? "Collapse sidebar" : "Expand sidebar"}
          title={expanded ? "Collapse sidebar" : "Expand sidebar"}
          data-testid="nav-toggle-btn"
          className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-[hsl(var(--surface-elevated))] shrink-0"
        >
          <ToggleIcon className="w-4 h-4" strokeWidth={1.75} />
        </button>
      </div>

      <nav className="flex-1 py-2 overflow-y-auto">
        {expanded ? (
          // ── Expanded: accordion groups ─────────────────────────────────
          MENU_GROUPS.map((group) => {
            const isOpen = openGroups.has(group.key);
            return (
              <div key={group.key} className="mb-0.5" data-testid={`nav-group-${group.key}`}>
                <button
                  onClick={() => handleGroupClick(group.key)}
                  aria-expanded={isOpen}
                  data-testid={`nav-group-trigger-${group.key}`}
                  className="w-full px-3 py-1.5 flex items-center justify-between text-[10px] font-bold text-muted-foreground hover:text-foreground hover:bg-[hsl(var(--surface-elevated))] transition-colors"
                >
                  <span>{group.label}</span>
                  <ChevronRight
                    className={`w-3 h-3 transition-transform duration-150 ${isOpen ? "rotate-90" : ""}`}
                    strokeWidth={2}
                  />
                </button>
                {isOpen && (
                  <div className="pb-1" data-testid={`nav-group-items-${group.key}`}>
                    {group.items.map(({ to, label, icon: Icon, testid }) => (
                      <NavLink
                        key={to}
                        to={to}
                        end={to === "/"}
                        data-testid={testid}
                        className={({ isActive }) =>
                          [
                            "flex items-center gap-3 py-2 px-4 text-xs font-bold transition-colors border-l-2",
                            isActive
                              ? "border-l-[hsl(var(--primary))] text-[hsl(var(--foreground))] bg-[hsl(var(--surface-elevated))]"
                              : "border-l-transparent text-muted-foreground hover:text-foreground hover:bg-[hsl(var(--surface-elevated))]",
                          ].join(" ")
                        }
                      >
                        <Icon className="w-4 h-4 shrink-0" strokeWidth={1.5} />
                        <span className="truncate">{label}</span>
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            );
          })
        ) : (
          // ── Collapsed: flat icon list (no group headers) ───────────────
          MENU_GROUPS.flatMap((g) => g.items).map(({ to, label, icon: Icon, testid }) => (
            <NavLink
              key={to}
              to={to}
              end={to === "/"}
              data-testid={testid}
              title={label}
              className={({ isActive }) =>
                [
                  "flex items-center justify-center py-2.5 px-3 transition-colors border-l-2",
                  isActive
                    ? "border-l-[hsl(var(--primary))] text-[hsl(var(--foreground))] bg-[hsl(var(--surface-elevated))]"
                    : "border-l-transparent text-muted-foreground hover:text-foreground hover:bg-[hsl(var(--surface-elevated))]",
                ].join(" ")
              }
            >
              <Icon className="w-4 h-4 shrink-0" strokeWidth={1.5} />
            </NavLink>
          ))
        )}
      </nav>
    </aside>
  );
};

export default LeftNav;
